import { useEffect, useState, useMemo, useCallback } from 'react';
import { Icon } from '@iconify/react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Card,
  CardBody,
  Skeleton,
  Chip,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Select,
  SelectItem,
  Spinner,
  Pagination,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { ProcessingHistoryModal } from '../../../components/modals/ProcessingHistoryModal';
import { supabase, getEdgeFunctionHeaders } from 'common/src/supabase';
import { EditableCell } from '../../../components/tables/editable-cell';
import { getShipByName, createShip } from 'common/src/api/ships';
import { createShipTrip, getShipTrips } from 'common/src/api/ship_trips';
import { debounce } from 'lodash';
import { SEASONS } from 'common/src/utils/definitions';
import { EmptyState, PageHeader, PageLayout } from '../../../components/ui';

interface ProcessingHistory {
  message_id: string;
  filename: string;
  processed_at: string;
  success: boolean;
  error?: string;
}

interface EmailAttachment {
  filename: string;
  mimeType: string;
  attachmentId: string;
  lastProcessing?: ProcessingHistory;
  processingHistory?: ProcessingHistory[];
}

interface Email {
  id: string;
  subject: string;
  from: string;
  date: string;
  attachments: EmailAttachment[];
}

// Add type for database email
interface DbEmail {
  message_id: string;
  subject: string;
  from: string;
  date: string;
  attachments: EmailAttachment[];
}

// Add an interface for Exporter
interface Exporter {
  id: number;
  name: string;
  excel_name: string;
  email_domain: string;
}

interface Species {
  id: number;
  name: string;
  code: string;
}

interface PreviewData {
  pallet_identificador: string;
  referencia: string;
  cliente: string;
  nave: string;
  mercado: string;
  nro_contenedor: string;
  fecha_venta: string;
  embalaje_etiqueta: string;
  formato: string;
  variedad: string;
  peso_bulto: number;
  calibre_color: string;
  bultos_cargados: number;
  precio_por_caja: number;
  kilos: number;
  moneda: string;
  clausula: string;
  ingreso_moneda_origen: number;
  tax_moneda_origen: number;
  comision_moneda_origen: number;
  costos_logisticos_moneda_origen: number;
  flete_usd: number;
  tipo_cambio: number;
  exportadora: string;
}

// Add new interface for the edit total modal
interface EditTotalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  currentValue: number;
  onConfirm: (newValue: number) => void;
}

// Add new component for editing totals
const EditTotalModal: React.FC<EditTotalModalProps> = ({
  isOpen,
  onClose,
  title,
  currentValue,
  onConfirm,
}) => {
  const [value, setValue] = useState(currentValue);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>
          <Input
            type='number'
            step='0.01'
            value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            label='Nuevo valor'
          />
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={onClose}>
            Cancelar
          </Button>
          <Button color='primary' onPress={() => onConfirm(value)}>
            Confirmar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Add these interfaces near the top with other interfaces
interface Ship {
  id: number;
  name: string;
  type: 'A' | 'M';
}

interface ShipTrip {
  id: number;
  ship_id: number;
  eta: string;
  etd: string;
  destination?: string;
  market_id?: string;
  market?: {
    id: string;
    name: string;
    country: string;
  };
}

// Add this component for ship type selection
const CreateShipModal = ({
  isOpen,
  onClose,
  shipName,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  shipName: string;
  onConfirm: (name: string, type: 'A' | 'M') => void;
}) => {
  const [type, setType] = useState<'A' | 'M'>('M');
  const [name, setName] = useState(shipName);

  useEffect(() => {
    if (isOpen) {
      setName(shipName);
    }
  }, [isOpen, shipName]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>Crear Nueva Nave</ModalHeader>
        <ModalBody>
          <div className='space-y-4'>
            <Input
              label='Nombre'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Select
              label='Tipo'
              selectedKeys={[type]}
              onChange={(e) => setType(e.target.value as 'A' | 'M')}
            >
              <SelectItem key='M' value='M'>
                Marítimo
              </SelectItem>
              <SelectItem key='A' value='A'>
                Aéreo
              </SelectItem>
            </Select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={onClose}>
            Cancelar
          </Button>
          <Button color='primary' onPress={() => onConfirm(name, type)}>
            Crear
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const SuperadminEmails = () => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [history, setHistory] = useState<ProcessingHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [finalUploadLoading, setFinalUploadLoading] = useState(false);
  const [processing, setProcessing] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] =
    useState<EmailAttachment | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [receiverModalOpen, setReceiverModalOpen] = useState(false);
  const [receivers, setReceivers] = useState<string[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);
  const [editableReferencia, setEditableReferencia] = useState('');
  const [editableFields, setEditableFields] = useState({
    referencia: '',
    nro_contenedor: '',
    season: '2024-2025',
    mercado_comparable: '',
    mercado_venta: '',
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingTotal, setEditingTotal] = useState<{
    type: 'tax' | 'comision' | 'costos' | 'flete';
    value: number;
  } | null>(null);
  const [shipTripSelectionModalOpen, setShipTripSelectionModalOpen] =
    useState(false);
  const [availableShipTrips, setAvailableShipTrips] = useState<ShipTrip[]>([]);
  const [selectedShipTripId, setSelectedShipTripId] = useState<number | null>(
    null
  );
  const [shipValidationStatus, setShipValidationStatus] = useState<{
    shipExists: boolean;
    shipTripsExist: boolean;
    shipName: string;
  }>({ shipExists: false, shipTripsExist: false, shipName: '' });
  const [ships, setShips] = useState<Ship[]>([]);
  const [selectedShip, setSelectedShip] = useState<Ship | null>(null);
  const [newShipName, setNewShipName] = useState('');
  const [showNewShipInput, setShowNewShipInput] = useState(false);
  const [shipTrips, setShipTrips] = useState<ShipTrip[]>([]);
  const [showNewTripForm, setShowNewTripForm] = useState(false);
  const [newTripDates, setNewTripDates] = useState({
    eta: '',
    etd: '',
    market_id: '' as string | number | null,
  });
  const [isShipSelectionPending, setIsShipSelectionPending] = useState(true);
  const [showCreateShipModal, setShowCreateShipModal] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 25;
  const [virtualEmails, setVirtualEmails] = useState<
    (Email & { currentAttachment: EmailAttachment })[]
  >([]);

  // Add states for exporters
  const [exporters, setExporters] = useState<Exporter[]>([]);
  const [selectedExporterId, setSelectedExporterId] = useState<number | null>(
    null
  );
  const [showExporterModal, setShowExporterModal] = useState(false);
  const [exporterSearchQuery, setExporterSearchQuery] = useState('');

  const [markets, setMarkets] = useState<
    { id: string; name: string; country: string }[]
  >([]);

  const [species, setSpecies] = useState<Species[]>([]);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<number | null>(
    null
  );
  const [showSpeciesModal, setShowSpeciesModal] = useState(false);

  useEffect(() => {
    if (selectedShipTripId) {
      setEditableFields((prev) => ({
        ...prev,
        mercado_venta:
          shipTrips.find((trip) => trip.id === selectedShipTripId)?.market_id ||
          '',
      }));
    }
  }, [selectedShipTripId, shipTrips]);

  const fetchEmails = async () => {
    console.log('fetching emails');
    setLoading(true);
    try {
      // Get emails from Gmail with date filter
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gmail`,
        {
          headers: {
            ...(await getEdgeFunctionHeaders()),
          },
        }
      );
      const data = await response.json();
      if (data.error) throw new Error(data.error);

      // Get processing history
      const { data: history } = await supabase
        .from('email_processing_history')
        .select('status, created_at, message_id, filename')
        .order('created_at', { ascending: false });

      // Create history map and combine with emails (same as before)
      const historyMap = history?.reduce((acc, record) => {
        const key = `${record.message_id}_${record.filename}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(record);
        return acc;
      }, {});

      // Map the emails with their histories
      const emailsWithHistory = data.emails.map((email: DbEmail) => ({
        id: email.message_id,
        subject: email.subject,
        from: email.from,
        date: email.date,
        attachments: email.attachments.map((att) => {
          const historyKey = `${email.message_id}_${att.filename}`;
          const attHistory = historyMap?.[historyKey] || [];
          return {
            ...att,
            lastProcessing: attHistory[0],
            processingHistory: attHistory,
          };
        }),
      }));

      setEmails(emailsWithHistory);
      setHistory(history || []);
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error('Error al cargar los emails');
    } finally {
      setLoading(false);
    }
  };

  const fetchReceivers = async () => {
    try {
      const { data, error } = await supabase
        .from('receivers') // Note: Changed from 'receivers' to 'receiver' - common Supabase singular naming convention
        .select('id, name')
        .order('name');

      if (error) throw error;
      setReceivers(data?.map((receiver) => receiver.name) || []);
    } catch (error) {
      console.error('Error fetching receivers:', error);
      toast.error('Error al cargar los recibidores');
    }
  };

  const fetchExporters = async () => {
    try {
      const { data, error } = await supabase
        .from('exporters')
        .select('id, name, excel_name, email_domain');

      if (error) throw error;

      setExporters(data || []);
    } catch (error) {
      console.error('Error fetching exporters:', error);
      toast.error('Error al cargar exportadores');
    }
  };

  const fetchMarkets = async () => {
    try {
      const { data, error } = await supabase
        .from('markets')
        .select('id, name, country')
        .order('name');

      if (error) throw error;
      setMarkets(data || []);
    } catch (error) {
      console.error('Error fetching markets:', error);
      toast.error('Error al cargar los mercados');
    }
  };

  const fetchSpecies = async () => {
    try {
      const { data, error } = await supabase
        .from('species')
        .select('*')
        .order('name');

      if (error) throw error;
      setSpecies(data || []);
    } catch (error) {
      console.error('Error fetching species:', error);
      toast.error('Error al cargar las especies');
    }
  };

  useEffect(() => {
    if (!isMounted) {
      fetchEmails();
      fetchReceivers();
      fetchExporters();
      fetchMarkets();
      fetchSpecies();
      setIsMounted(true);
    }
  }, [isMounted]);

  const handleProcess = async (
    email: Email & { currentAttachment: EmailAttachment }
  ) => {
    // Only process the specific attachment that was clicked
    const attachment = email.currentAttachment;

    if (
      !attachment.mimeType.includes('excel') &&
      !attachment.mimeType.includes('spreadsheet') &&
      !attachment.filename.toLowerCase().endsWith('.xlsx') &&
      !attachment.filename.toLowerCase().endsWith('.xls')
    ) {
      toast.error('No hay archivos Excel para procesar');
      return;
    }

    console.log(email);

    setSelectedEmail({
      ...email,
      attachments: [attachment], // Only include the clicked attachment
    });

    // Check if the sender is from cherrymarket.cl
    if (email.from.includes('@cherrymarket.cl')) {
      setSelectedExporterId(null); // Reset selected exporter
      setShowExporterModal(true);
    } else {
      setSelectedSpeciesId(null);
      setReceiverModalOpen(true);
    }
  };

  // Add new function to handle species selection
  const handleSpeciesSelection = (speciesId: number) => {
    setSelectedSpeciesId(speciesId);
    setShowSpeciesModal(false);
    setReceiverModalOpen(true);
  };

  // Transform emails data to flatten attachments
  const flattenedEmails = useMemo(
    () =>
      emails.flatMap((email) =>
        email.attachments.map((attachment) => ({
          ...email,
          currentAttachment: attachment,
        }))
      ),
    [emails]
  );

  const filteredEmails = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return flattenedEmails.filter(
      (email) =>
        email.subject.toLowerCase().includes(searchLower) ||
        email.from.toLowerCase().includes(searchLower) ||
        email.currentAttachment.filename.toLowerCase().includes(searchLower)
    );
  }, [flattenedEmails, searchQuery]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredEmails.slice(start, end);
  }, [filteredEmails, page, rowsPerPage]);

  const columns = [
    { name: 'Acciones', uid: 'actions' },
    { name: 'De', uid: 'from' },
    { name: 'Asunto', uid: 'subject' },
    { name: 'Fecha', uid: 'date' },
    { name: 'Archivo', uid: 'attachment' },
    { name: 'Estado', uid: 'status' },
  ];

  const handleCellEdit = (
    rowIndex: number,
    field: string,
    value: string | number
  ) => {
    const updatedData = [...previewData];
    updatedData[rowIndex] = {
      ...updatedData[rowIndex],
      [field]: value,
    };

    // Recalculate totals if needed
    if (
      field === 'bultos_cargados' ||
      field === 'precio_por_caja' ||
      field === 'peso_bulto'
    ) {
      updatedData[rowIndex].kilos =
        updatedData[rowIndex].peso_bulto *
        updatedData[rowIndex].bultos_cargados;

      updatedData[rowIndex].ingreso_moneda_origen =
        updatedData[rowIndex].bultos_cargados *
        updatedData[rowIndex].precio_por_caja;
    }

    setPreviewData(updatedData);
  };

  const renderCell = (
    item: Email & { currentAttachment: EmailAttachment },
    columnKey: React.Key
  ) => {
    switch (columnKey) {
      case 'from':
        return <div className='font-medium'>{item.from}</div>;
      case 'subject':
        return <div className='max-w-md truncate'>{item.subject}</div>;
      case 'date':
        return (
          <div>
            {new Date(item.date).toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        );
      case 'attachment':
        return (
          <div className='flex items-center gap-2 max-w-[300px]'>
            {(item.currentAttachment.mimeType.includes('excel') ||
              item.currentAttachment.mimeType.includes('spreadsheet')) && (
              <Icon icon='tabler:file-spreadsheet' className='text-success flex-shrink-0' />
            )}
            <span
              className='truncate text-sm'
              title={item.currentAttachment.filename}
            >
              {item.currentAttachment.filename}
            </span>
          </div>
        );
      case 'status':
        return (
          <Chip
            variant='flat'
            color={
              !item.currentAttachment.processingHistory?.length
                ? 'default'
                : item.currentAttachment.lastProcessing?.status === 'error'
                  ? 'danger'
                  : item.currentAttachment.lastProcessing?.status === 'success'
                    ? 'success'
                    : 'warning'
            }
          >
            {!item.currentAttachment.processingHistory?.length
              ? 'No Procesado'
              : item.currentAttachment.lastProcessing?.status === 'error'
                ? 'Error'
                : 'Procesado'}
          </Chip>
        );
      case 'actions':
        console.log('attach', item.currentAttachment);
        const isExcelAttachment =
          item.currentAttachment.mimeType.includes('excel') ||
          item.currentAttachment.mimeType.includes('spreadsheet') ||
          item.currentAttachment.filename.toLowerCase().endsWith('.xlsx') ||
          item.currentAttachment.filename.toLowerCase().endsWith('.xls');
        return (
          <div className='flex gap-2'>
            {isExcelAttachment && (
              <>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  onClick={() => {
                    setSelectedAttachment(item.currentAttachment);
                    setHistoryModalOpen(true);
                  }}
                >
                  <Icon icon='tabler:history' />
                </Button>
                <Button
                  color='primary'
                  size='sm'
                  onClick={() => handleProcess(item)}
                  isLoading={processing.includes(item.id)}
                >
                  Procesar
                </Button>
              </>
            )}
          </div>
        );
    }
  };

  // Modify the processWithExporter function to show receiver modal after selecting exporter
  const processWithExporter = async (exporterId: number) => {
    if (!selectedEmail) return;

    // Store the selected exporter ID for later use
    setSelectedExporterId(exporterId);

    // Close the exporter modal and open the receiver modal
    setShowExporterModal(false);
    setReceiverModalOpen(true);
  };

  // Modify the processWithReceiver to use the selected exporter if available
  const processWithReceiver = async (receiverName: string) => {
    if (!selectedSpeciesId) {
      toast.error('Debe seleccionar una especie');
      return;
    }
    if (!selectedEmail) return;

    setProcessing((prev) => [...prev, selectedEmail.id]);
    const toastId = toast.loading('Procesando archivos...');

    try {
      const attachment = selectedEmail.currentAttachment;

      // Create request body with or without exporterId and include speciesId
      const requestBody = {
        messageId: selectedEmail.id,
        filename: attachment.filename,
        receiverName: receiverName,
        speciesId: selectedSpeciesId,
        ...(selectedExporterId ? { exporterId: selectedExporterId } : {}),
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gmail-process`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(await getEdgeFunctionHeaders()),
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();
      if (data.error) throw new Error(data.error);

      // Store preview data and show preview modal directly
      setPreviewData(data.data);
      setShowPreviewModal(true);

      toast.update(toastId, {
        render: 'Archivo procesado correctamente',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error processing:', error);
      toast.update(toastId, {
        render: `Error al procesar: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setProcessing((prev) => prev.filter((id) => id !== selectedEmail.id));
      setReceiverModalOpen(false);
      // Reset the selected exporter ID and species ID after processing
      setSelectedExporterId(null);
    }
  };

  const handleFinalUpload = async () => {
    setFinalUploadLoading(true);
    try {
      if (!selectedShipTripId) {
        toast.error('Debe seleccionar un viaje');
        return;
      }

      if (!editableFields.referencia) {
        toast.error('Debe ingresar una referencia');
        return;
      }

      if (!editableFields.mercado_venta) {
        toast.error('Debe seleccionar un mercado de venta');
        return;
      }

      if (!selectedSpeciesId) {
        toast.error('Debe seleccionar una especie');
        return;
      }

      const dataWithUpdates = previewData.map((row) => ({
        ...row,
        ...editableFields,
        ship_trip_id: selectedShipTripId,
        species_id: selectedSpeciesId,
      }));

      const response = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/upload-settlements-with-ship-trip`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(await getEdgeFunctionHeaders()),
          },
          body: JSON.stringify({
            data: dataWithUpdates,
          }),
        }
      );

      const result = await response.json();
      if (result.error) throw new Error(result.error);

      toast.success('Datos subidos correctamente');
      setShowPreviewModal(false);
      setSelectedEmail(null);
      setPreviewData([]);
      fetchEmails(); // Refresh the email list
    } catch (error) {
      console.error('Error uploading data:', error);
      toast.error('Error al subir los datos');
    } finally {
      setFinalUploadLoading(false);
    }
  };

  useEffect(() => {
    if (previewData.length > 0) {
      setEditableFields({
        referencia: previewData[0]?.referencia || '',
        mercado: previewData[0]?.mercado || '',
        nro_contenedor: previewData[0]?.nro_contenedor || '',
        season: previewData[0]?.season || '2024-2025',
      });
    }
  }, [previewData]);

  const previewColumns = [
    {
      name: 'Pallet',
      uid: 'pallet_identificador',
      editable: true,
    },
    {
      name: 'Fecha Vta',
      uid: 'fecha_venta',
      editable: true,
    },
    {
      name: 'Variedad',
      uid: 'variedad',
      editable: true,
    },
    {
      name: 'Etiqueta',
      uid: 'embalaje_etiqueta',
      editable: true,
    },
    {
      name: 'Formato',
      uid: 'formato',
      editable: true,
    },
    {
      name: 'Calibre',
      uid: 'calibre_color',
      editable: true,
    },
    {
      name: 'Color',
      uid: 'color',
      editable: true,
    },
    {
      name: 'Categoria',
      uid: 'categoria',
      editable: true,
    },
    {
      name: 'Peso Bulto',
      uid: 'peso_bulto',
      editable: true,
    },
    {
      name: 'Bultos',
      uid: 'bultos_cargados',
      editable: true,
    },
    {
      name: 'Kilos',
      uid: 'kilos',
      editable: false,
    },
    {
      name: `Precio (${previewData[0]?.moneda})`,
      uid: 'precio_por_caja',
      editable: true,
    },
    {
      name: `Total (${previewData[0]?.moneda})`,
      uid: 'ingreso_moneda_origen',
      editable: false,
    },
  ];

  const handleEditTotal = (type: 'tax' | 'comision' | 'costos' | 'flete') => {
    const currentValue = previewData.reduce((sum, row) => {
      switch (type) {
        case 'tax':
          return sum + row.tax_moneda_origen;
        case 'comision':
          return sum + row.comision_moneda_origen;
        case 'costos':
          return sum + row.costos_logisticos_moneda_origen;
        case 'flete':
          return sum + row.flete_usd;
      }
    }, 0);

    setEditingTotal({ type, value: currentValue });
    onOpen();
  };

  const handleConfirmEdit = (newValue: number) => {
    if (!editingTotal) return;

    const updatedData = [...previewData];
    const totalBase = previewData.reduce((sum, row) => {
      return (
        sum +
        (editingTotal.type === 'costos' || editingTotal.type === 'flete'
          ? row.kilos
          : row.ingreso_moneda_origen)
      );
    }, 0);

    updatedData.forEach((row) => {
      const weight =
        editingTotal.type === 'costos' || editingTotal.type === 'flete'
          ? row.kilos / totalBase
          : row.ingreso_moneda_origen / totalBase;

      switch (editingTotal.type) {
        case 'tax':
          row.tax_moneda_origen = newValue * weight;
          break;
        case 'comision':
          row.comision_moneda_origen = newValue * weight;
          break;
        case 'costos':
          row.costos_logisticos_moneda_origen = newValue * weight;
          break;
        case 'flete':
          row.flete_usd = newValue * weight;
          break;
      }
    });

    setPreviewData(updatedData);
    setEditingTotal(null);
    onClose();
  };

  // Add this function to fetch ships
  const fetchShips = async () => {
    try {
      const { data: ships, error } = await supabase
        .from('ships')
        .select('*')
        .order('name');

      if (error) throw error;
      setShips(ships || []);
    } catch (error) {
      console.error('Error fetching ships:', error);
      toast.error('Error al cargar las naves');
    }
  };

  // Add this function to fetch ship trips
  const fetchShipTrips = async (shipId: number) => {
    try {
      const { data: trips, error } = await supabase
        .from('ship_trips')
        .select(
          `
          *,
          market:markets!ship_trips_market_id_fkey(id, name, country),
          origin_market:markets!ship_trips_origin_market_id_fkey(id, name, country)
        `
        )
        .eq('ship_id', shipId)
        .order('etd', { ascending: false });

      if (error) throw error;
      setShipTrips(trips || []);
    } catch (error) {
      console.error('Error fetching ship trips:', error);
      toast.error('Error al cargar los viajes');
    }
  };

  // Update the handleCreateShip function
  const handleCreateShip = async (name: string, type: 'A' | 'M') => {
    try {
      const newShip = await createShip({
        name,
        type,
        company: null,
        created_at: new Date().toISOString(),
        trips_count: 0,
      });

      if (!newShip) throw new Error('Error creating ship');

      await fetchShips();
      setSelectedShip(newShip);
      setShowCreateShipModal(false);
      setNewShipName('');
      toast.success('Nave creada exitosamente');
    } catch (error) {
      console.error('Error creating ship:', error);
      toast.error('Error al crear la nave');
    }
  };

  // Update the handleCreateTrip function
  const handleCreateTrip = async () => {
    try {
      if (!selectedShip) throw new Error('No ship selected');
      if (!newTripDates.market_id)
        throw new Error('Debes seleccionar un mercado');

      const newTrip = await createShipTrip({
        ship_id: selectedShip.id,
        eta: new Date(newTripDates.eta).toISOString(),
        etd: new Date(newTripDates.etd).toISOString(),
        market_id: newTripDates.market_id,
      });

      if (!newTrip) throw new Error('Error creating ship trip');

      await fetchShipTrips(selectedShip.id);
      setSelectedShipTripId(newTrip.id);
      setShowNewTripForm(false);
      setNewTripDates({ eta: '', etd: '', market_id: '' });
      toast.success('Viaje creado exitosamente');
    } catch (error) {
      console.error('Error creating trip:', error);
      toast.error('Error al crear el viaje');
    }
  };

  // Update the useEffect that handles ship matching
  useEffect(() => {
    const handleShipMatching = async () => {
      if (ships.length > 0 && previewData.length > 0) {
        const shipName = previewData[0]?.nave;
        console.log('Checking ship match for:', shipName);

        if (shipName) {
          setIsShipSelectionPending(true); // Start selection process
          const normalizedShipName = shipName.trim().toLowerCase();
          const matchingShip = ships.find(
            (ship) => ship.name.trim().toLowerCase() === normalizedShipName
          );

          console.log('Found matching ship:', matchingShip);

          if (matchingShip) {
            console.log('Setting selected ship:', matchingShip);
            setSelectedShip(matchingShip);
            await fetchShipTrips(matchingShip.id);
            setNewShipName('');
            setShowNewShipInput(false);
          } else {
            setSelectedShip(null);
            setNewShipName(shipName);
          }
          setIsShipSelectionPending(false); // End selection process
        }
      }
    };

    handleShipMatching();
  }, [ships, previewData]);

  // Update the useEffect that runs when preview modal opens
  useEffect(() => {
    const initializeShipData = async () => {
      if (showPreviewModal) {
        await fetchShips();
        await fetchMarkets(); // Fetch markets when modal opens
      } else {
        // Reset ship-related state when modal closes
        setSelectedShip(null);
        setNewShipName('');
        setShowNewShipInput(false);
        setSelectedShipTripId(null);
      }
    };

    initializeShipData();
  }, [showPreviewModal]);

  // 5. Add debounced search
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value);
    }, 300),
    []
  );

  // Add a filtered exporters list based on search query
  const filteredExporters = useMemo(() => {
    const searchLower = exporterSearchQuery.toLowerCase();
    return exporters.filter(
      (exporter) =>
        exporter.name.toLowerCase().includes(searchLower) ||
        exporter.excel_name.toLowerCase().includes(searchLower) ||
        (exporter.email_domain &&
          exporter.email_domain.toLowerCase().includes(searchLower))
    );
  }, [exporters, exporterSearchQuery]);

  if (loading) {
    return (
      <PageLayout>
        <PageHeader title='Emails' />
        <Card>
          <CardBody>
            <div className='space-y-4'>
              {[...Array(5)].map((_, i) => (
                <div key={i} className='grid grid-cols-5 gap-4'>
                  {[...Array(5)].map((_, j) => (
                    <Skeleton key={j} className='h-12 rounded-lg' />
                  ))}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title='Emails'
        actions={
          <Button
            variant='flat'
            onClick={fetchEmails}
            isLoading={loading}
          >
            Actualizar
          </Button>
        }
      />

      <div className='mb-4'>
        <Input
          placeholder='Buscar por asunto, remitente o nombre de archivo...'
          onChange={(e) => debouncedSearch(e.target.value)}
          startContent={<Icon icon='tabler:search' className='text-default-300' />}
          className='max-w-md'
        />
      </div>

      <Table
        aria-label='Emails table'
        bottomContent={
          <div className='flex w-full justify-center'>
            <Pagination
              isCompact
              showControls
              showShadow
              color='primary'
              page={page}
              total={Math.ceil(filteredEmails.length / rowsPerPage)}
              onChange={(page) => setPage(page)}
            />
          </div>
        }
      >
        <TableHeader columns={columns}>
          {(column) => (
            <TableColumn key={column.uid}>{column.name}</TableColumn>
          )}
        </TableHeader>
        <TableBody items={paginatedData} emptyContent={<EmptyState title='No hay emails' />}>
          {(item) => (
            <TableRow key={`${item.id}-${item.currentAttachment.attachmentId}`}>
              {(columnKey) => (
                <TableCell>{renderCell(item, columnKey)}</TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>

      {selectedAttachment && (
        <ProcessingHistoryModal
          isOpen={historyModalOpen}
          onClose={() => {
            setHistoryModalOpen(false);
            setSelectedAttachment(null);
          }}
          history={selectedAttachment.processingHistory || []}
          filename={selectedAttachment.filename}
        />
      )}

      {receiverModalOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4'>
          <div className='bg-background rounded-lg p-6 max-w-md w-full'>
            <h2 className='text-xl font-bold mb-4'>Seleccionar Especie</h2>
            <div className='space-y-4'>
              <p className='text-sm text-default-400'>
                Por favor seleccione la especie para este procesamiento:
              </p>
              <div className='max-h-60 overflow-y-auto space-y-2'>
                <select
                  className='w-full p-2 border rounded'
                  value={selectedSpeciesId || ''}
                  onChange={(e) =>
                    handleSpeciesSelection(Number(e.target.value))
                  }
                >
                  <option value=''>Seleccione una especie</option>
                  {species.map((specie) => (
                    <option key={specie.id} value={specie.id}>
                      {specie.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <h2 className='text-xl font-bold mb-4'>Seleccionar Recibidor</h2>
            <div className='space-y-2'>
              {receivers.map((receiver) => (
                <button
                  key={receiver}
                  onClick={() => processWithReceiver(receiver)}
                  className='w-full p-2 text-left hover:bg-default-200 rounded transition'
                >
                  {receiver}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setReceiverModalOpen(false);
                setSelectedEmail(null);
              }}
              className='mt-4 w-full p-2 bg-default-200 rounded'
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        size='5xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader>
            Vista Previa de Liquidación -{' '}
            {selectedEmail?.currentAttachment.filename}
          </ModalHeader>
          <ModalBody>
            <div className='mb-6 space-y-4'>
              <div className='p-4 bg-default-100 rounded-lg'>
                <div className='flex flex-col gap-4'>
                  {isShipSelectionPending ? (
                    <div className='flex items-center justify-center p-8 gap-2'>
                      <Spinner size='sm' />
                      <span className='text-default-500'>
                        Cargando información de nave...
                      </span>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Select
                          label='Nave'
                          placeholder='Seleccionar nave'
                          selectedKeys={
                            selectedShip ? [selectedShip.id.toString()] : []
                          }
                          onChange={(e) => {
                            const ship = ships.find(
                              (s) => s.id === Number(e.target.value)
                            );
                            if (ship) {
                              // console.log('Manual ship selection:', ship);
                              setSelectedShip(ship);
                              fetchShipTrips(ship.id);
                              setSelectedShipTripId(null);
                              setNewShipName('');
                              setShowNewShipInput(false);
                            }
                          }}
                        >
                          {ships.map((ship) => (
                            <SelectItem
                              key={ship.id}
                              value={ship.id.toString()}
                            >
                              {ship.name}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>

                      {previewData[0]?.nave && !selectedShip && (
                        <div className='flex items-center justify-between p-3 bg-warning-50 border border-warning-200 rounded-lg'>
                          <span className='text-warning-700'>
                            La nave "{previewData[0].nave}" no existe en el
                            sistema. Por favor, créela para continuar o busca en
                            la lista si es que existe con otro nombre.
                          </span>
                          <Button
                            variant='flat'
                            size='sm'
                            onClick={() => {
                              console.log(
                                'Creating new ship:',
                                previewData[0].nave
                              );
                              setNewShipName(previewData[0].nave);
                              setShowCreateShipModal(true);
                            }}
                          >
                            Crear Nave
                          </Button>
                        </div>
                      )}

                      {selectedShip && (
                        <div className='space-y-4'>
                          <div className='flex gap-4 items-end'>
                            <div className='flex-1'>
                              <Select
                                label='Viaje'
                                placeholder='Seleccionar viaje'
                                selectedKeys={
                                  selectedShipTripId
                                    ? [selectedShipTripId.toString()]
                                    : []
                                }
                                onChange={(e) =>
                                  setSelectedShipTripId(Number(e.target.value))
                                }
                              >
                                {shipTrips.map((trip) => (
                                  <SelectItem
                                    key={trip.id}
                                    value={trip.id.toString()}
                                  >
                                    {`ETD: ${trip.etd.split('T')[0]} - ETA: ${
                                      trip.eta.split('T')[0]
                                    } - Mercado: ${trip.market?.name}, ${
                                      trip.market?.country
                                    }`}
                                  </SelectItem>
                                ))}
                              </Select>
                            </div>
                            <Button
                              size='sm'
                              onClick={() => setShowNewTripForm(true)}
                            >
                              Nuevo Viaje
                            </Button>
                          </div>

                          {showNewTripForm && (
                            <div className='space-y-2 rounded-medium border border-default-200 p-4'>
                              <h4 className='font-semibold'>
                                Nuevo Viaje para {selectedShip.name}
                              </h4>
                              <div className='flex gap-4'>
                                <Input
                                  type='date'
                                  label='ETD'
                                  value={newTripDates.etd}
                                  onChange={(e) =>
                                    setNewTripDates((prev) => ({
                                      ...prev,
                                      etd: e.target.value,
                                    }))
                                  }
                                  className='flex-1'
                                />
                                <Input
                                  type='date'
                                  label='ETA'
                                  value={newTripDates.eta}
                                  onChange={(e) =>
                                    setNewTripDates((prev) => ({
                                      ...prev,
                                      eta: e.target.value,
                                    }))
                                  }
                                  className='flex-1'
                                />
                                <div className='flex-1'>
                                  <Select
                                    label='Mercado'
                                    placeholder='Seleccionar mercado'
                                    selectedKeys={
                                      newTripDates.market_id
                                        ? [newTripDates.market_id]
                                        : []
                                    }
                                    onChange={(e) => {
                                      setNewTripDates((prev) => ({
                                        ...prev,
                                        market_id: e.target.value,
                                      }));
                                    }}
                                  >
                                    {markets.map((market) => (
                                      <SelectItem
                                        key={market.id}
                                        value={market.id}
                                      >
                                        {`${market.name}, ${market.country}`}
                                      </SelectItem>
                                    ))}
                                  </Select>
                                </div>
                              </div>
                              <div className='flex gap-2 justify-end mt-2'>
                                <Button
                                  variant='light'
                                  onClick={() => {
                                    setShowNewTripForm(false);
                                    setNewTripDates({
                                      eta: '',
                                      etd: '',
                                      market_id: '',
                                    });
                                  }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  color='primary'
                                  onClick={handleCreateTrip}
                                  disabled={
                                    !newTripDates.eta ||
                                    !newTripDates.etd ||
                                    !newTripDates.market_id
                                  }
                                >
                                  Crear Viaje
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              {/* <div className="p-4 bg-default-100 rounded-lg">
                <div className="flex flex-col gap-4">
                  <div>
                    <Select
                      label="Especie"
                      placeholder="Seleccionar especie"
                      selectedKeys={selectedSpeciesId ? [selectedSpeciesId.toString()] : []}
                      onChange={(e) => setSelectedSpeciesId(Number(e.target.value))}
                    >
                      {species.map((specie) => (
                        <SelectItem key={specie.id} value={specie.id.toString()}>
                          {specie.name}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </div>
              </div> */}
            </div>

            <Table aria-label='Preview data table' selectionMode='none'>
              <TableHeader>
                {previewColumns.map((column) => (
                  <TableColumn key={column.uid}>{column.name}</TableColumn>
                ))}
              </TableHeader>
              <TableBody>
                {previewData.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {previewColumns.map((column) => (
                      <TableCell key={column.uid}>
                        {column.editable ? (
                          <EditableCell
                            initialValue={row[column.uid] ?? ''}
                            onSave={(value) =>
                              handleCellEdit(rowIndex, column.uid, value)
                            }
                            type={
                              typeof row[column.uid] === 'number'
                                ? 'number'
                                : 'text'
                            }
                            className={`${
                              typeof row[column.uid] === 'number'
                                ? 'text-right'
                                : ''
                            } border border-divider rounded-md px-2 py-1 bg-default-100 hover:bg-default-200 transition-colors cursor-pointer`}
                          />
                        ) : (
                          <div
                            className={`${
                              typeof row[column.uid] === 'number'
                                ? 'text-right'
                                : ''
                            } px-2 py-1`}
                          >
                            {row[column.uid]}
                          </div>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className='mt-4 p-4 bg-default-100 rounded-lg'>
              <h3 className='font-bold mb-2'>
                Resumen
                <Chip
                  className='ml-2'
                  size='sm'
                  color={
                    previewData.some((row) => row.flete_usd > 0)
                      ? 'warning'
                      : 'success'
                  }
                >
                  {previewData.some((row) => row.flete_usd > 0) ? 'FOB' : 'CIF'}
                </Chip>
              </h3>
              <div className='grid grid-cols-4 gap-4'>
                <div>
                  <p className='text-sm text-default-500'>Especie</p>
                  <p className='font-semibold'>
                    {species.find((s) => s.id === selectedSpeciesId)?.name ||
                      'No seleccionada'}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Temporada</p>
                  <Select
                    selectedKeys={
                      editableFields.season ? [editableFields.season] : []
                    }
                    onChange={(e) =>
                      setEditableFields((prev) => ({
                        ...prev,
                        season: e.target.value,
                      }))
                    }
                    className='mt-1'
                  >
                    {Object.values(SEASONS).map((season) => (
                      <SelectItem key={season.name} value={season.name}>
                        {season.name}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Referencia</p>
                  <Input
                    value={editableFields.referencia}
                    onChange={(e) =>
                      setEditableFields((prev) => ({
                        ...prev,
                        referencia: e.target.value,
                      }))
                    }
                    placeholder='Ingrese referencia'
                    className='mt-1'
                  />
                </div>
                <div>
                  <p className='text-sm text-default-500'>Exportadora</p>
                  <p className='font-semibold'>{previewData[0]?.exportadora}</p>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Cliente</p>
                  <p className='font-semibold'>{previewData[0]?.cliente}</p>
                </div>

                <div>
                  <p className='text-sm text-default-500'>Mercado Comparable</p>
                  <Select
                    selectedKeys={
                      editableFields.mercado_comparable
                        ? [editableFields.mercado_comparable]
                        : []
                    }
                    onChange={(e) =>
                      setEditableFields((prev) => ({
                        ...prev,
                        mercado_comparable: e.target.value,
                      }))
                    }
                    placeholder='Seleccione mercado'
                    className='mt-1'
                  >
                    {/* <SelectItem key="null" value="null">NINGUNO</SelectItem> */}
                    <SelectItem key='CNCAN' value='CNCAN'>
                      GUANGZHOU
                    </SelectItem>
                    <SelectItem key='CNSHA' value='CNSHA'>
                      SHANGHAI
                    </SelectItem>
                  </Select>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Mercado Venta</p>
                  <Select
                    selectedKeys={
                      editableFields.mercado_venta
                        ? [editableFields.mercado_venta]
                        : []
                    }
                    onChange={(e) =>
                      setEditableFields((prev) => ({
                        ...prev,
                        mercado_venta: e.target.value,
                      }))
                    }
                    placeholder='Seleccione mercado'
                    className='mt-1'
                  >
                    {markets.map((market) => (
                      <SelectItem key={market.id} value={market.id}>
                        {`${market.name}, ${market.country}`}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Contenedor</p>
                  <Input
                    value={editableFields.nro_contenedor}
                    onChange={(e) =>
                      setEditableFields((prev) => ({
                        ...prev,
                        nro_contenedor: e.target.value,
                      }))
                    }
                    placeholder='Ingrese contenedor'
                    className='mt-1'
                  />
                </div>
                <div>
                  <p className='text-sm text-default-500'>Tipo de Cambio</p>
                  <p className='font-semibold'>
                    {previewData[0]?.tipo_cambio?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Total Bultos</p>
                  <p className='font-semibold'>
                    {previewData
                      .reduce((sum, row) => sum + row.bultos_cargados, 0)
                      .toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Total Kilos</p>
                  <p className='font-semibold'>
                    {previewData
                      .reduce((sum, row) => sum + row.kilos, 0)
                      .toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-500'>
                    Total {previewData[0]?.moneda}
                  </p>
                  <p className='font-semibold'>
                    {previewData
                      .reduce((sum, row) => sum + row.ingreso_moneda_origen, 0)
                      .toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Total Tax</p>
                  <p
                    className='font-semibold cursor-pointer hover:text-brand-900 flex items-center gap-1'
                    onClick={() => handleEditTotal('tax')}
                    title='Click para editar'
                  >
                    {previewData
                      .reduce((sum, row) => sum + row.tax_moneda_origen, 0)
                      .toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}{' '}
                    (
                    {(
                      (previewData.reduce(
                        (sum, row) => sum + row.tax_moneda_origen,
                        0
                      ) /
                        previewData.reduce(
                          (sum, row) => sum + row.ingreso_moneda_origen,
                          0
                        )) *
                      100
                    ).toFixed(1)}
                    %)
                    <span className='text-xs text-default-300'>✎</span>
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Total Comisión</p>
                  <p
                    className='font-semibold cursor-pointer hover:text-brand-900 flex items-center gap-1'
                    onClick={() => handleEditTotal('comision')}
                    title='Click para editar'
                  >
                    {previewData
                      .reduce((sum, row) => sum + row.comision_moneda_origen, 0)
                      .toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}{' '}
                    (
                    {(
                      (previewData.reduce(
                        (sum, row) => sum + row.comision_moneda_origen,
                        0
                      ) /
                        previewData.reduce(
                          (sum, row) => sum + row.ingreso_moneda_origen,
                          0
                        )) *
                      100
                    ).toFixed(1)}
                    %)
                    <span className='text-xs text-default-300'>✎</span>
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Total Otros Costos</p>
                  <p
                    className='font-semibold cursor-pointer hover:text-brand-900 flex items-center gap-1'
                    onClick={() => handleEditTotal('costos')}
                    title='Click para editar'
                  >
                    {previewData
                      .reduce(
                        (sum, row) => sum + row.costos_logisticos_moneda_origen,
                        0
                      )
                      .toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    <span className='text-xs text-default-300'>✎</span>
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Flete USD</p>
                  <p
                    className='font-semibold cursor-pointer hover:text-brand-900 flex items-center gap-1'
                    onClick={() => handleEditTotal('flete')}
                    title='Click para editar'
                  >
                    {previewData
                      .reduce((sum, row) => sum + row.flete_usd, 0)
                      .toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    <span className='text-xs text-default-300'>✎</span>
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-500'>Total FOB USD</p>
                  <p className='font-semibold'>
                    {(
                      (previewData.reduce(
                        (sum, row) => sum + row.ingreso_moneda_origen,
                        0
                      ) -
                        previewData.reduce(
                          (sum, row) => sum + row.tax_moneda_origen,
                          0
                        ) -
                        previewData.reduce(
                          (sum, row) => sum + row.comision_moneda_origen,
                          0
                        ) -
                        previewData.reduce(
                          (sum, row) =>
                            sum + row.costos_logisticos_moneda_origen,
                          0
                        )) /
                        previewData[0]?.tipo_cambio -
                      previewData.reduce((sum, row) => sum + row.flete_usd, 0)
                    ).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => setShowPreviewModal(false)}
              isLoading={finalUploadLoading}
              disabled={finalUploadLoading}
            >
              Cancelar
            </Button>
            <Button
              color='primary'
              onPress={handleFinalUpload}
              isLoading={finalUploadLoading}
              disabled={finalUploadLoading || !selectedShipTripId}
            >
              Confirmar y Subir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {editingTotal && (
        <EditTotalModal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setEditingTotal(null);
          }}
          title={`Editar ${
            editingTotal.type === 'tax'
              ? 'Tax'
              : editingTotal.type === 'comision'
                ? 'Comisión'
                : editingTotal.type === 'costos'
                  ? 'Costos Logísticos'
                  : 'Flete USD'
          }`}
          currentValue={editingTotal.value}
          onConfirm={handleConfirmEdit}
        />
      )}

      <CreateShipModal
        isOpen={showCreateShipModal}
        onClose={() => setShowCreateShipModal(false)}
        shipName={newShipName}
        onConfirm={handleCreateShip}
      />

      {/* Modify the Exporter Selection Modal */}
      <Modal
        isOpen={showExporterModal}
        onClose={() => setShowExporterModal(false)}
      >
        <ModalContent>
          <ModalHeader>Seleccionar Exportador</ModalHeader>
          <ModalBody>
            <div className='space-y-4'>
              <p className='text-sm text-default-400'>
                Este correo proviene de @cherrymarket.cl. Por favor seleccione
                el exportador:
              </p>

              {/* Add search input */}
              <Input
                placeholder='Buscar exportador...'
                value={exporterSearchQuery}
                onChange={(e) => setExporterSearchQuery(e.target.value)}
                startContent={
                  <svg
                    className='text-default-300'
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <circle cx='11' cy='11' r='8'></circle>
                    <line x1='21' y1='21' x2='16.65' y2='16.65'></line>
                  </svg>
                }
                className='mb-2'
                size='sm'
              />

              <div className='max-h-60 overflow-y-auto space-y-2'>
                {filteredExporters.length > 0 ? (
                  filteredExporters.map((exporter) => (
                    <button
                      key={exporter.id}
                      onClick={() => processWithExporter(exporter.id)}
                      className='w-full p-2 text-left hover:bg-default-200 rounded transition'
                    >
                      <div className='font-medium'>{exporter.name}</div>
                      {exporter.email_domain && (
                        <div className='text-xs text-default-400'>
                          {exporter.email_domain}
                        </div>
                      )}
                    </button>
                  ))
                ) : (
                  <div className='text-center py-3 text-default-400'>
                    No se encontraron exportadores
                  </div>
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                setShowExporterModal(false);
                setExporterSearchQuery(''); // Clear search when closing
              }}
            >
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminEmails;
